import { useMutation, useQueryClient } from "@tanstack/react-query";
import { sendChatMessage } from "../services/discuss.service";
import type { ChatMessage, ChatHistoryResponse } from "../types/discuss.types";

/**
 * Hook to send a chat message and get AI response
 */
export const useSendMessage = (documentId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: sendChatMessage,
    mutationKey: ["sendChatMessage", documentId],
    onMutate: async (newChatMessage) => {
      await queryClient.cancelQueries({ queryKey: ["chatHistory", documentId] });

      const previousChatHistory = queryClient.getQueryData<ChatHistoryResponse>([
        "chatHistory",
        documentId,
      ]);

      queryClient.setQueryData<ChatHistoryResponse>(["chatHistory", documentId], (oldData) => {
        if (oldData == null) return oldData;
        const newUserMessage: ChatMessage = {
          question: newChatMessage.data,
          response: "",
          creation_date: new Date().toISOString(),
          related_questions: [],
        };
        return {
          ...oldData,
          prev_chat: [...oldData.prev_chat, newUserMessage],
        };
      });

      return { previousChatHistory };
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData<ChatHistoryResponse>(["chatHistory", documentId], (oldData) => {
        if (oldData == null) return oldData;
        const updatedMessages = [...oldData.prev_chat];
        const lastMessage = updatedMessages[updatedMessages.length - 1];
        if (lastMessage != null && lastMessage.question === variables.data) {
          lastMessage.response = data.response;
          lastMessage.creation_date = data.creation_date;
          lastMessage.related_questions = data.related_questions;
        }
        return { ...oldData, prev_chat: updatedMessages };
      });
    },
    onError: (error, newChatMessage, context) => {
      console.error("Failed to send message:", error);
      queryClient.setQueryData<ChatHistoryResponse>(
        ["chatHistory", documentId],
        context?.previousChatHistory
      );
      queryClient.setQueryData<ChatHistoryResponse>(["chatHistory", documentId], (oldData) => {
        if (oldData == null) return oldData;
        const updatedMessages = [...oldData.prev_chat];
        const lastMessage = updatedMessages[updatedMessages.length - 1];
        if (lastMessage != null && lastMessage.question === newChatMessage.data) {
          lastMessage.response =
            "Sorry, I encountered an error while processing your message. Please try again.";
        }
        return { ...oldData, prev_chat: updatedMessages };
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["chatHistory", documentId] });
    },
  });
};
